import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Card,
  Button,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  FormControlLabel,
  MenuItem,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogContentText,
  useMediaQuery,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { uploadImage } from "../../../Components/UploadFile/upload";
import CloseIcon from "@mui/icons-material/Close";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumberForDisplay,
} from "../../../Components/Validation/validationUtils";
import {
  useCreateTeamMutation,
  useDeleteEmployeeScheduleMutation,
  useGetDepartmentQuery,
  useGetPositionQuery,
} from "../../../Api/attoDeskApi";
import { FaTrash } from "react-icons/fa";
import AddIcon from "@mui/icons-material/Add";
import {
  IPosition,
  ITeam,
  ITeamCreate,
} from "../../../Api/Interface/api.interface";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NewDepartement from "../../Department/Edit/index";
import { IDepartment } from "../../../Api/Interface/api.interface";
import Delete from "@mui/icons-material/Delete";
import NewPosition from "../../Position/Edit/index";
import { toProperCase } from "../../../Components/Validation/toProperCase";

interface TimeSlot {
  id: number;
  startTime: string;
  endTime: string;
  hourlySalary: string;
}

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: ITeam;
}

const toAllCaps = (text: string | null | undefined): string => {
  if (!text) return "";
  return text.toUpperCase();
};

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(
    undefined
  );
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [newCompany, { isLoading }] = useCreateTeamMutation();
  const [showPassword, setShowPassword] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [openDialog, setOpenDialog] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<{
    index: number;
    slotIndex: number;
  } | null>(null);

  const handleClose = () => {
    handleCloseDialog(false);
  };

  const { data: departmentData, isLoading: departmentLoading } =
    useGetDepartmentQuery({
      searchText: "",
    });

  const { data: positionData, isLoading: positionLoading } =
    useGetPositionQuery({
      searchText: "",
    });

  const [openDepartment, setOpenDepartment] = useState(false);
  const [openPosition, setOpenPosition] = useState(false);

  const departmentList = useMemo(() => {
    return departmentData?.data as IDepartment[];
  }, [departmentData?.data]);

  const positionList = useMemo(() => {
    return positionData?.data as IPosition[];
  }, [positionData?.data]);

  const handlePhoneChange = (
    fieldName: string,
    value: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const formattedValue = formatPhoneNumberForDisplay(value);
    setFieldValue(fieldName, formattedValue);
  };

  const formik = useFormik<ITeamCreate>({
    initialValues: {
      id: String(data?.id) || "",
      teamId: data?.teamId || "",
      teamPositionId: String(data?.teamPositionId) || "",
      teamHourlySalary: data?.teamHourlySalary || "",
      teamOtHourlySalary: data?.teamOtHourlySalary || "",
      teamDepartmentId: String(data?.teamDepartmentId) || "",
      teamMobileNum: data?.teamMobileNum
        ? formatPhoneNumberForDisplay(data.teamMobileNum)
        : "",
      teamFirstName: data?.teamFirstName || "",
      teamLastName: data?.teamLastName || "",
      teamAltMobileNum: data?.teamAltMobileNum
        ? formatPhoneNumberForDisplay(data.teamAltMobileNum)
        : "",
      teamImg: data?.teamImg || "",
      teamStreet: data?.teamStreet || "",
      teamCity: data?.teamCity || "",
      teamProvince: data?.teamProvince || "",
      teamPostalCode: data?.teamPostalCode || "",
      teamAddress: data?.teamAddress || "",
      teamEmail: data?.teamEmail || "",
      teamUserName: data?.teamUserName || "",
      teamPassWord: data?.teamPassWord || "",
      teamDescription: data?.teamDescription || "",
      availability: data?.availability?.map((available) => ({
        day: available.day,
        timeSlots: available.timeSlots?.map((slot) => ({
          id: slot.id,
          startTime: slot.startTime || "",
          endTime: slot.endTime || "",
          hourlySalary: slot.hourlySalary || "",
          spansNextDay: slot.spansNextDay || false,
        })) || [
          {
            id: null,
            startTime: "",
            endTime: "",
            hourlySalary: "",
            spansNextDay: false,
          },
        ],
      })) || [
        {
          day: "Sunday",
          timeSlots: [],
        },
        {
          day: "Monday",
          timeSlots: [],
        },
        {
          day: "Tuesday",
          timeSlots: [],
        },
        {
          day: "Wednesday",
          timeSlots: [],
        },
        {
          day: "Thursday",
          timeSlots: [],
        },
        {
          day: "Friday",
          timeSlots: [],
        },
        {
          day: "Saturday",
          timeSlots: [],
        },
      ],
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: { [key: string]: string } = {};

      const emailError = validateEmail(values.teamEmail);
      if (emailError) {
        errors.teamEmail = emailError;
      }

      const mobileNumberError = validatePhoneNumber(values.teamMobileNum);
      if (mobileNumberError) {
        errors.teamMobileNum = mobileNumberError;
      }

      const altMobileNumberError = validatePhoneNumber(values.teamAltMobileNum);
      if (altMobileNumberError) {
        errors.teamAltMobileNum = altMobileNumberError;
      }

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        let teamImg: string | undefined = data?.teamImg;

        if (selectedImage) {
          setIsImageUploading(true);
          formData.append("teamImg", selectedImage);
          teamImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const updatedData = {
          id: Number(values.id),
          teamId: values.teamId,
          teamPositionId: Number(values.teamPositionId),
          teamHourlySalary: values.teamHourlySalary,
          teamOtHourlySalary: values.teamOtHourlySalary,
          teamDepartmentId: Number(values.teamDepartmentId),
          teamMobileNum: values.teamMobileNum.replace(/-/g, ""),
          teamAltMobileNum: values.teamAltMobileNum.replace(/-/g, ""),
          teamFirstName: toProperCase(values.teamFirstName),
          teamLastName: toProperCase(values.teamLastName),
          teamCity: toProperCase(values.teamCity),
          teamAddress: values.teamAddress,
          teamEmail: values.teamEmail?.trim().toLowerCase(),
          teamDescription: values.teamDescription,
          teamImg,
          availability: values.availability,
          teamUserName: values.teamUserName,
          teamPassWord: values.teamPassWord,
          teamStreet: toProperCase(values.teamStreet),
          teamProvince: toProperCase(values.teamProvince),
          teamPostalCode: toAllCaps(values.teamPostalCode),
        };

        const response = await newCompany(updatedData).unwrap();
        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
          setSelectedImage(undefined);
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleDeleteClick = (index: number, slotIndex: number) => {
    setSlotToDelete({ index, slotIndex });
    setOpenDialog(true);
  };

  const handleDeleteCancel = () => {
    setOpenDialog(false);
    setSlotToDelete(null);
  };

  const formValid = useMemo(() => {
    return (formik.values.teamFirstName || formik.values.teamLastName) === "" ||
      (formik.values.teamFirstName || formik.values.teamLastName) === undefined
      ? false
      : true;
  }, [formik]);

  const resizeImage = (
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, {
                type: file.type,
              });
              resolve(resizedFile);
            } else {
              reject(new Error("Canvas is empty"));
            }
          }, file.type);
        };

        img.onerror = (error) => reject(error);
      };
    });
  };

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;

    if (files && files.length > 0) {
      const file = files[0];
      const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
      const maxSize = 2048 * 1024;

      if (!validTypes.includes(file.type)) {
        showErrorMessage("The file must be an image (jpeg, png, jpg, gif).");
        setSelectedImage(undefined);
      } else if (file.size > maxSize) {
        showErrorMessage("The file must be smaller than 2 MB.");
        setSelectedImage(undefined);
      } else {
        try {
          const resizedImage = await resizeImage(file, 500, 500);
          setSelectedImage(resizedImage);
        } catch (error) {
          showErrorMessage("Failed to resize the image.");
          setSelectedImage(undefined);
        }
      }
    } else {
      setSelectedImage(undefined);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [deleteCategory] = useDeleteEmployeeScheduleMutation();

  const handleDeleteConfirm = async () => {
    if (slotToDelete) {
      const { index, slotIndex } = slotToDelete;
      const slotId = formik.values.availability[index].timeSlots[slotIndex].id;

      try {
        await deleteCategory(String(slotId));

        // Explicitly type timeSlots as TimeSlot[]
        const timeSlots = formik.values.availability[index]
          .timeSlots as TimeSlot[];

        // Filter out the deleted time slot, considering the possibility of `null` id
        formik.setFieldValue(
          `availability.${index}.timeSlots`,
          timeSlots.filter((slot, i) => i !== slotIndex) // Ensure that we remove the right slot by index
        );

        setOpenDialog(false); // Close dialog after deletion
      } catch (error) {
        console.error("Error deleting time slot:", error);
      }
    }
  };

  if (departmentLoading || positionLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-describedby="alert-dialog-description"
        classes={{ paper: "inherit" }}
        fullScreen={isMobile}
        fullWidth={isMobile}
        maxWidth="lg"
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="10vh"
        >
          <CircularProgress />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobile}
      fullWidth={isMobile}
      maxWidth="lg"
    >
      <Grid className={classes.Dialog}>
        <Grid container spacing={3}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Card className={classes.stepperCard}>
              <Grid
                container
                justifyContent={"space-between"}
                spacing={5}
                sx={{ borderBottom: 2, padding: 2 }}
                className={classes.titleGrid}
              >
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  pt={2}
                  alignContent={"center"}
                >
                  <Typography className={classes.cardTitle}>Edit Schedule</Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={3}>
                  {formik.values.availability.map((day, index) => (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <Card className={classes.card}>
                        <Grid
                          container
                          justifyContent={"space-between"}
                          spacing={5}
                          sx={{ padding: 2 }}
                          className={classes.titleGrid}
                        >
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            pt={2}
                            alignContent={"center"}
                          >
                            <Typography className={classes.cardTitle}>
                              {day.day} 
                            </Typography>
                          </Grid>
                        </Grid>

                        <Grid container spacing={3} p={"24px"}>
                          <Grid
                            item
                            lg={12}
                            md={12}
                            sm={12}
                            xs={12}
                            key={day.day}
                          >
                            <Grid container spacing={2} alignItems="center">
                              <FieldArray
                                name={`availability.${index}.timeSlots`}
                              >
                                {({ push, remove }) => (
                                  <>
                                    <Grid item lg={12} md={12} sm={12} xs={12}>
                                      <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        spacing={2}
                                      >
                                        <TableContainer>
                                          <Table>
                                            <TableHead>
                                              <TableRow>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  Start Time
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  End Time
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                ></TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                >
                                                  Hourly Salary
                                                </TableCell>
                                                <TableCell
                                                  className={
                                                    classes.tablheaderCreate
                                                  }
                                                  sx={{ textAlign: "right" }}
                                                >
                                                  Action
                                                </TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {formik.values.availability[
                                                index
                                              ].timeSlots.map(
                                                (slot, slotIndex) => (
                                                  <TableRow key={slotIndex}>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="Start Time"
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.startTime`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="End Time"
                                                        size="small"
                                                        type="time"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.endTime`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <FormControlLabel
                                                        control={
                                                          <Checkbox
                                                            // checked={ Boolean(`availability.${index}.timeSlots.${slotIndex}.spansNextDay`)}
                                                            checked={
                                                              formik.values
                                                                .availability[
                                                                index
                                                              ].timeSlots[
                                                                slotIndex
                                                              ].spansNextDay ||
                                                              false
                                                            }
                                                            onChange={(e) =>
                                                              formik.setFieldValue(
                                                                `availability.${index}.timeSlots.${slotIndex}.spansNextDay`,
                                                                e.target.checked
                                                              )
                                                            }
                                                          />
                                                        }
                                                        label="Ends next day"
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <TextField
                                                        placeholder="Hourly Salary"
                                                        size="small"
                                                        type="number"
                                                        {...formik.getFieldProps(
                                                          `availability.${index}.timeSlots.${slotIndex}.hourlySalary`
                                                        )}
                                                        className={
                                                          classes.textField
                                                        }
                                                        InputProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                        InputLabelProps={{
                                                          sx: {
                                                            fontSize: 14,
                                                          },
                                                        }}
                                                      />
                                                    </TableCell>
                                                    <TableCell>
                                                      <Grid
                                                        container
                                                        spacing={1}
                                                        sx={{
                                                          display: "flex",
                                                          justifyContent:
                                                            "flex-end",
                                                        }}
                                                      >
                                                        <Grid item>
                                                          <IconButton
                                                            color="error"
                                                            onClick={() =>
                                                              handleDeleteClick(
                                                                index,
                                                                slotIndex
                                                              )
                                                            }
                                                            // disabled={
                                                            //   formik.values
                                                            //     .availability[
                                                            //     index
                                                            //   ].timeSlots
                                                            //     .length === 1
                                                            // }
                                                          >
                                                            <FaTrash />
                                                          </IconButton>
                                                        </Grid>
                                                      </Grid>
                                                    </TableCell>
                                                  </TableRow>
                                                )
                                              )}
                                            </TableBody>
                                          </Table>
                                        </TableContainer>
                                      </Grid>
                                    </Grid>

                                    <Grid
                                      item
                                      lg={12}
                                      md={12}
                                      sm={12}
                                      xs={12}
                                      display="flex"
                                      sx={{
                                        justifyContent: "flex-end",
                                        alignItems: "flex-end",
                                        textAlign: "end",
                                      }}
                                    >
                                      <Button
                                        variant="contained"
                                        // startIcon={<AddIcon />}
                                        className={classes.addButton}
                                        onClick={() =>
                                          push({
                                            id: null,
                                            startTime: "",
                                            endTime: "",
                                            spansNextDay: false,
                                            hourlySalary:
                                              formik.values.teamHourlySalary,
                                          })
                                        }
                                      >
                                        <AddIcon />
                                      </Button>
                                    </Grid>
                                  </>
                                )}
                              </FieldArray>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  ))}

                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={"24px"}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          sx={{ display: "flex", justifyContent: "flex-end" }}
                        >
                          <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            className={classes.addButton}
                            onClick={handleClose}
                          >
                            Cancel
                          </Button>
                          <Box m={0.5}></Box>

                          <Button
                            variant="contained"
                            startIcon={
                              isLoading ? (
                                <CircularProgress
                                  size={18}
                                  className={classes.isLoading}
                                />
                              ) : (
                                <SaveAltIcon />
                              )
                            }
                            className={classes.addButton}
                            onClick={() => formik.handleSubmit()}
                            disabled={!formValid}
                          >
                            Save
                          </Button>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                </Grid>
                {openDepartment && (
                  <NewDepartement
                    handleCloseDialog={() => setOpenDepartment(false)}
                    openModel={openDepartment}
                  />
                )}

                {openPosition && (
                  <NewPosition
                    handleCloseDialog={() => setOpenPosition(false)}
                    openModel={openPosition}
                  />
                )}

                <Dialog
                  open={openDialog}
                  onClose={handleDeleteCancel}
                  sx={{ padding: 5, minWidth: 450 }}
                >
                  <Grid className={classes.Dialog}>
                    <Grid
                      container
                      justifyContent={"space-between"}
                      spacing={5}
                      sx={{ borderBottom: 2, padding: 2 }}
                      className={classes.titleGrid}
                    >
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        pt={2}
                        alignContent={"center"}
                      >
                        <Typography className={classes.cardTitle}>
                          Delete Time Slot
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <DialogContent>
                    <DialogContentText sx={{ pt: 3 }}>
                      Are you sure you want to delete this time slot?
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions
                    sx={{ paddingRight: "24px", p: 4, py: 3, gap: 2 }}
                  >
                    <Grid container spacing={3}>
                      <Grid
                        item
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        sx={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          className={classes.addButton}
                          onClick={handleDeleteCancel}
                        >
                          Cancel
                        </Button>
                        <Box m={0.5}></Box>

                        <Button
                          variant="contained"
                          startIcon={<Delete />}
                          className={classes.addButton}
                          onClick={handleDeleteConfirm}
                        >
                          Confirm
                        </Button>
                      </Grid>
                    </Grid>
                  </DialogActions>
                </Dialog>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
